import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, CheckCheck, X, ExternalLink, Calendar } from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 60_000; // 1 minuto
const PAGE_SIZE = 20;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 19).replace('T', ' ');
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
};

export default function NotificationBell({ canSee = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  const fetchNotifs = useCallback(async () => {
    if (!canSee) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/notifications?limit=${PAGE_SIZE}`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      const sorted = [...(d.items || [])].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setItems(sorted);
      setUnread(d.unread_count || 0);
      setHasMore(!!d.has_more);
    } catch { /* ignore */ }
  }, [canSee]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || items.length === 0) return;
    const oldest = items[items.length - 1]?.created_at;
    if (!oldest) return;
    setLoadingMore(true);
    try {
      const url = `${API_URL}/api/admin/notifications?limit=${PAGE_SIZE}&before=${encodeURIComponent(oldest)}`;
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      const incoming = (d.items || []).filter((n) => !items.some((it) => it.id === n.id));
      const merged = [...items, ...incoming].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      setItems(merged);
      setHasMore(!!d.has_more);
    } catch { /* ignore */ } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!canSee) return;
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [canSee, fetchNotifs]);

  // ESC fecha o modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Recarrega ao abrir
  useEffect(() => {
    if (open) fetchNotifs();
  }, [open, fetchNotifs]);

  const markRead = async (notifId) => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/${notifId}/read`, {
        method: 'POST',
        headers: authHeaders(),
      });
      setItems((prev) => prev.map((n) => (n.id === notifId ? { ...n, read: true } : n)));
      setUnread((c) => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/read-all`, {
        method: 'POST',
        headers: authHeaders(),
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  const handleNavigateRelated = (n) => {
    if (n.type === 'audit_anomaly') {
      if (!n.read) markRead(n.id);
      setOpen(false);
      navigate('/work-orders');
    }
  };

  if (!canSee) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="notification-bell-button"
        aria-label={`Notificações (${unread} não lidas)`}
        title={unread > 0 ? `${unread} não lida(s)` : 'Notificações'}
        className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white transition-colors relative"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm"
          data-testid="notification-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div
            className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl"
            data-testid="notification-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-modal-title"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-800">
              <div className="flex items-center gap-2 min-w-0">
                <Bell size={20} className="text-yellow-400 shrink-0" />
                <h3 id="notification-modal-title" className="text-base sm:text-lg font-semibold text-white truncate">
                  Notificações
                </h3>
                {unread > 0 && (
                  <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-300 rounded-full shrink-0">
                    {unread} nova(s)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {unread > 0 && (
                  <button
                    onClick={markAllRead}
                    className="hidden sm:flex text-xs text-gray-300 hover:text-white items-center gap-1 px-2 py-1 rounded hover:bg-gray-800 transition-colors"
                    data-testid="notification-mark-all-read"
                    title="Marcar todas como lidas"
                  >
                    <CheckCheck size={14} /> Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-gray-400 hover:text-white p-1 -m-1"
                  data-testid="notification-modal-close"
                  aria-label="Fechar"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Body com scroll interno */}
            <div className="flex-1 overflow-y-auto" data-testid="notification-modal-body">
              {items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                  <Bell size={32} className="mx-auto mb-2 text-gray-600" />
                  Nenhuma notificação ainda
                </div>
              ) : (
                <ul className="divide-y divide-gray-800">
                  {items.map((n) => {
                    const isAudit = n.type === 'audit_anomaly';
                    const hasRelated = isAudit && !!n.audit_run_id;
                    return (
                      <li
                        key={n.id}
                        className={`px-5 py-4 ${n.read ? '' : 'bg-yellow-500/5'}`}
                        data-testid={`notification-item-${n.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <AlertTriangle
                            size={18}
                            className={`mt-0.5 shrink-0 ${n.read ? 'text-gray-500' : 'text-yellow-400'}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <p
                                className={`text-sm break-words ${n.read ? 'text-gray-300' : 'text-white font-semibold'}`}
                                data-testid={`notification-title-${n.id}`}
                              >
                                {n.title}
                              </p>
                              {n.type && (
                                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 bg-gray-800 text-gray-400 rounded-full shrink-0">
                                  {n.type.replace(/_/g, ' ')}
                                </span>
                              )}
                            </div>
                            <p
                              className="text-sm text-gray-300 mt-1 whitespace-pre-wrap break-words leading-relaxed"
                              data-testid={`notification-message-${n.id}`}
                            >
                              {n.message || <span className="text-gray-500 italic">Sem mensagem detalhada.</span>}
                            </p>
                            <p
                              className="text-xs text-gray-500 mt-2 flex items-center gap-1.5"
                              data-testid={`notification-datetime-${n.id}`}
                            >
                              <Calendar size={12} /> {formatDateTime(n.created_at)}
                            </p>
                            {n.audit_run_id && (
                              <p className="text-[11px] text-gray-600 mt-1 font-mono break-all">
                                Auditoria ID: {n.audit_run_id}
                              </p>
                            )}

                            {/* Ações por notificação */}
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              {hasRelated && (
                                <button
                                  onClick={() => handleNavigateRelated(n)}
                                  className="px-3 py-1.5 text-xs bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                                  data-testid={`notification-navigate-${n.id}`}
                                >
                                  <ExternalLink size={13} /> Ver Ordens de Serviço
                                </button>
                              )}
                              {!n.read && (
                                <button
                                  onClick={() => markRead(n.id)}
                                  className="px-3 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
                                  data-testid={`notification-mark-read-${n.id}`}
                                >
                                  <CheckCheck size={13} /> Marcar como lida
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Botão Carregar mais */}
              {items.length > 0 && hasMore && (
                <div className="px-5 py-3 border-t border-gray-800 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="notification-load-more"
                    className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center gap-2"
                  >
                    {loadingMore ? 'Carregando…' : 'Carregar mais'}
                  </button>
                </div>
              )}
            </div>

            {/* Footer com ações (botão "Marcar todas" sempre acessível no mobile) */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-gray-800 bg-[#0d0d0d]">
              <div className="text-xs text-gray-500">
                {items.length > 0 && `${items.length} no total · ${unread} não lida(s)`}
              </div>
              <div className="flex gap-2">
                {unread > 0 && (
                  <button
                    onClick={markAllRead}
                    className="sm:hidden px-3 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                    data-testid="notification-mark-all-read-mobile"
                  >
                    <CheckCheck size={16} /> Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  data-testid="notification-modal-dismiss"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
